// src/components/personal/sections/OrdersSection/error/error-handlers.ts
import React from 'react';
import { APIError } from '../types/types';
import { ERROR_CODES } from './error-constants';
import { ErrorLogger } from '@/utils/error-logger';

/**
 * Types of API errors
 */
export type ApiErrorType = 
  | 'network'
  | 'validation'
  | 'not_found'
  | 'auth'
  | 'server'
  | 'order'
  | 'payment'
  | 'export'
  | 'unknown';

// Mapping error codes to translation keys
const ERROR_TRANSLATION_KEYS: Record<string, string> = {
  'NETWORK_ERROR': 'errors.network',
  'VALIDATION_ERROR': 'errors.validation',
  'NOT_FOUND': 'errors.not_found',
  'UNAUTHORIZED': 'errors.unauthorized',
  'FORBIDDEN': 'errors.forbidden',
  'SERVER_ERROR': 'errors.server',
  'UNKNOWN_ERROR': 'errors.unknown',
  'ORDER_UPDATE_ERROR': 'errors.status_update_error',
  'PAYMENT_ERROR': 'errors.payment_add_error',
  'EXPORT_ERROR': 'errors.export_error'
};

/**
 * Display error notification
 */
export const displayErrorNotification = (error: APIError): void => {
  // Get translation key for error code
  const translateKey = ERROR_TRANSLATION_KEYS[error.code] || 'errors.unknown';
  
  // In a real integration, this would call toast or another notification system
  console.error(`Error (${error.code}):`, error.message, `Translation key: ${translateKey}`);
  
  // FOR REAL USAGE
  /*
  import { toast } from 'react-toastify';
  
  toast(() => React.createElement(MessageComponent, {
    type: 'error',
    translateKey: translateKey,
    message: error.message
  }), {
    position: "top-right",
    autoClose: 5000
  });
  */
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: APIError): boolean => {
  return error.code === 'NETWORK_ERROR';
};

/**
 * Check if error is a validation error
 */
export const isValidationError = (error: APIError): boolean => {
  return error.code === 'VALIDATION_ERROR';
};

/**
 * Get error type from code
 */
export const getErrorType = (error: APIError): ApiErrorType => {
  switch (error.code) {
    case ERROR_CODES.NETWORK_ERROR:
      return 'network';
    case ERROR_CODES.VALIDATION_ERROR:
      return 'validation';
    case ERROR_CODES.NOT_FOUND:
      return 'not_found';
    case ERROR_CODES.UNAUTHORIZED:
    case ERROR_CODES.FORBIDDEN:
      return 'auth';
    case ERROR_CODES.SERVER_ERROR:
      return 'server';
    case ERROR_CODES.ORDER_UPDATE_ERROR:
      return 'order';
    case ERROR_CODES.PAYMENT_ERROR:
      return 'payment';
    case ERROR_CODES.EXPORT_ERROR:
      return 'export';
    default:
      return 'unknown';
  }
};

/**
 * Check if request should be retried
 */
export const shouldRetry = (error: APIError): boolean => {
  const errorType = getErrorType(error);
  return errorType === 'network' || errorType === 'server';
};

/**
 * Get translation key for error code
 */
export const getErrorTranslationKey = (errorCode: string): string => {
  return ERROR_TRANSLATION_KEYS[errorCode] || 'errors.unknown';
};

interface ErrorDisplayProps {
  error: APIError | null;
  className?: string;
}

// Импортируем тип MessageType
export type MessageType = 'info' | 'success' | 'warning' | 'error';

/**
 * ErrorDisplay component
 */
export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, className }) => {
  if (!error) return null;
  
  const errorTranslateKey = getErrorTranslationKey(error.code);
  
  // Импортируем MessageComponent, когда это используется в компоненте
  const MessageComponent = require('@/components/ui/Message').default;
  
  return React.createElement(MessageComponent, {
    type: 'error' as MessageType,
    message: error.message,
    translateKey: errorTranslateKey,
    className
  });
};

/**
 * Comprehensive API error handling
 */
export const handleApiError = async (
  error: APIError, 
  source: string,
  showNotification: boolean = true
): Promise<ApiErrorType> => {
  // Log error
  await ErrorLogger.logError(error, source);
  
  // Determine error type
  const errorType = getErrorType(error);
  
  // Notify user (if needed)
  if (showNotification) {
    displayErrorNotification(error);
  }
  
  // Handle based on error type
  switch (errorType) {
    case 'auth':
      // Redirect to login page - используем только в браузерном окружении
      if (typeof document !== 'undefined') {
        const currentPath = document.location.pathname;
        if (currentPath !== '/login') {
          document.location.href = '/login';
        }
      }
      break;
  }
  
  return errorType;
};